import React, { useEffect, useState, useRef } from "react";
import { VideoUploader } from "../../components/Video/VideoUploader";
import { STATUS } from "../../lib/constants";
import {
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { replaceVideoUrl, useDecodedTokenResult } from "../../lib/utils";
import { MdOutlinePersonalVideo } from "react-icons/md";
import {
  useAddVideoMutation,
  useGetVideoByIdMutation,
  useUpdateVideoMutation,
} from "../../api/api";
import { useToast } from "../../@/components/ui/use-toast";
import InfoTooltip from "../../components/common/InfoTooltip";
import Hls from "hls.js";

interface VideoDetails {
  title: string;
  // description: string;
  filePath: string;
  deletedVideoFileObj?: {
    filePath: string;
  };
}

interface AddEditVideoProps {
  isForAddVideo: boolean;
  isForEditVideo: boolean;
}

const AddEditVideo: React.FC<AddEditVideoProps> = ({
  isForAddVideo,
  isForEditVideo,
}) => {
  const [videoDetails, setVideoDetails] = useState<VideoDetails>({
    title: "",
    // description: '',
    filePath: "",
  });
  const { toast } = useToast();
  const { vendorId } = useDecodedTokenResult();
  const { id } = useParams();
  const [addVideo, { isLoading: addVideoLoader }] = useAddVideoMutation();
  const [getVideoById, { isLoading: getVideoByIdLoader }] =
    useGetVideoByIdMutation();
  const [updateVideo, { isLoading: updateVideoLoader }] =
    useUpdateVideoMutation();

  const [isDisabled, setIsDisabled] = useState(false);
  const navigate = useNavigate();
  const { setShowAlertOnRouteChange } = useOutletContext<any>();
  const [isLoading, setIsLoading] = useState(isForEditVideo);
  const UserRole = useDecodedTokenResult();
  const isAdmin = UserRole.role === "ADMIN";
  const [originalVendorId, setOriginalVendorId] = useState<number | undefined>(
    undefined
  );
  const [videoOrientation, setVideoOrientation] = useState<string>("");
  const [searchParams] = useSearchParams();
  const isViewOnly = searchParams.get("viewonly") === "true";
  const videoRef = useRef<HTMLVideoElement>(null);
  const [statusId, setStatusId] = useState<number>(0);

  useEffect(() => {
    if (isForEditVideo && id) {
      setIsLoading(true);
      (async () => {
        try {
          const response = await getVideoById({
            body: {
              id: Number(id),
            },
          }).unwrap();
          if (!response.data) {
            toast({
              variant: "destructive",
              title: "Error",
              description:
                "Failed to fetch video details. Please try again later.",
              showIcon: true,
            });
            navigate("/videos");
            return;
          }
          // Store the original vendorId
          setOriginalVendorId(response.data.vendorId);
          setVideoDetails({
            title: response.data.title || "",
            filePath: replaceVideoUrl(response.data.filePath),
          });
          setStatusId(response.data.statusId);
        } catch (error) {
          toast({
            variant: "destructive",
            title: "Error",
            description:
              "Failed to fetch video details. Please try again later.",
            showIcon: true,
          });
          navigate("/videos");
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [isForEditVideo, id]);

  useEffect(() => {
    if (videoDetails.filePath && videoRef.current) {
      const video = videoRef.current;
      const isHLS = videoDetails.filePath.includes(".m3u8");

      if (isHLS) {
        if (Hls.isSupported()) {
          const hls = new Hls();
          hls.loadSource(videoDetails.filePath);
          hls.attachMedia(video);
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            video
              .play()
              .catch((error) => console.log("Playback failed:", error));
          });
        }
        // For browsers with native HLS support (Safari)
        else if (video.canPlayType("application/vnd.apple.mpegurl")) {
          video.src = videoDetails.filePath;
        }
      } else {
        // Handle regular video formats (mp4, etc.)
        video.src = videoDetails.filePath;
      }
    }
  }, [videoDetails.filePath]);

  const handleSubmit = async (status: STATUS, e: any) => {
    try {
      e.preventDefault();
      if (!videoDetails.title.trim() || !videoDetails.filePath.trim()) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Please complete all required fields.",
          showIcon: true,
        });
        return;
      }
      setIsDisabled(true);
      let response;

      if (isForAddVideo) {
        response = await addVideo({
          addSimpleVideoDto: {
            vendorId: isAdmin ? undefined : vendorId,
            // statusId: status,
            title: videoDetails.title,
            filePath: videoDetails.filePath,
          },
        }).unwrap();
      } else if (isForEditVideo && id) {
        // Include deletedVideoFileObj only if there's a deleted video
        const updatePayload = {
          ...videoDetails,
          vendorId: isAdmin
            ? originalVendorId
              ? originalVendorId
              : undefined
            : vendorId,
          id: Number(id),
          title: videoDetails.title,
        };

        // Remove deletedVideoFileObj if it's empty
        if (!videoDetails.deletedVideoFileObj?.filePath) {
          delete updatePayload.deletedVideoFileObj;
        }

        response = await updateVideo({
          updateSimpleVideoDto: updatePayload,
        }).unwrap();
      }

      if (response) {
        toast({
          variant: "success",
          title: "Success",
          description: isForAddVideo
            ? "Video added successfully."
            : "Video updated successfully.",
          showIcon: true,
        });
        navigate("/videos");
      }
      setIsDisabled(false);
    } catch (error) {
      setIsDisabled(false);
      toast({
        variant: "destructive",
        title: "Error",
        description: isForAddVideo
          ? "Failed to add video. Please try again."
          : "Failed to update video. Please try again.",
        showIcon: true,
      });
    }
  };
  const tooltipContent = {
    videoTitle:
      "This is the title of the video that will be displayed on the video page.",
    videoDescription:
      "This is the description of the video that will be displayed on the video page.",
  };

  // Add skeleton loading UI
  if (isLoading) {
    return (
      <div className="bg-gray-50">
        <div className="flex gap-1 mt-1 p-6 space-x-2 bg-customOffWhiteColor">
          <div className="h-10 w-24 bg-gray-200 rounded-lg animate-pulse"></div>
          <div className="h-10 w-24 bg-gray-200 rounded-lg animate-pulse"></div>
        </div>

        <main className="grid grid-cols-2 gap-8 px-6">
          <div className="bg-white p-4 shadow rounded">
            <div className="h-6 w-32 bg-gray-200 rounded animate-pulse mb-4"></div>
            <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mb-2"></div>
            <div className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="bg-white p-4 shadow rounded">
            <div className="h-48 w-full bg-gray-200 rounded animate-pulse"></div>
          </div>
        </main>
      </div>
    );
  }

  // Add this helper function before the return statement
  const isFormValid = videoDetails.title.trim() && videoDetails.filePath.trim();

  const checkVideoOrientation = (videoElement: HTMLVideoElement) => {
    const orientation =
      videoElement.videoWidth > videoElement.videoHeight
        ? "landscape"
        : "portrait";
    setVideoOrientation(orientation);
  };

  return (
    <div className="bg-gray-50 pb-28 md:pb-0 ">
      {!isViewOnly ? (
        <div className="flex gap-1 mt-1 p-4 sm:p-6 space-x-2 bg-customOffWhiteColor">
          <button
            className={`flex text-sm font-normal items-center justify-center ${
              isFormValid
                ? "bg-customGreenColor text-white"
                : "bg-customGreenColor/50 text-white cursor-not-allowed"
            } px-3 sm:px-4 py-2 rounded-lg`}
            type="button"
            disabled={isDisabled || !isFormValid}
            onClick={(e: any) => handleSubmit(STATUS.ProductActive, e)}
          >
            {!addVideoLoader && (
              <img
                src="/Images/ProductFormImages/plane.png"
                className="w-4 h-4 sm:w-5 sm:h-5 mr-1"
                alt="Save"
              />
            )}
            {addVideoLoader ? "Saving..." : "Save"}
          </button>
          <button
            className="flex text-sm font-normal items-center justify-center bg-transparent text-customGrayText border border-customGrayText px-3 sm:px-4 py-2 rounded-lg"
            disabled={isDisabled}
            type="button"
            onClick={() => {
              setShowAlertOnRouteChange(false);
              navigate("/videos");
            }}
          >
            <img
              src="/Images/ProductFormImages/close-square.png"
              className="w-4 h-4 sm:w-5 sm:h-5 mr-1"
              alt="Cancel"
            />
            Cancel
          </button>
        </div>
      ) : (
        <div className="flex mt-1 p-4 sm:p-6 bg-customOffWhiteColor">
          <button
            className="flex text-sm font-normal items-center justify-center bg-transparent text-customGrayText border border-customGrayText px-3 sm:px-4 py-2 rounded-lg"
            type="button"
            onClick={() => navigate("/videos")}
          >
            <img
              src="/Images/ProductFormImages/close-square.png"
              className="w-4 h-4 sm:w-5 sm:h-5 mr-1"
              alt="Back"
            />
            Back
          </button>
        </div>
      )}

      <main
        className={`grid ${
          isViewOnly ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"
        } gap-4 sm:gap-6 lg:gap-8 p-4 sm:px-6`}
      >
        {!isViewOnly && (
          <div className="bg-white p-3 sm:p-4 shadow rounded">
            <h2 className="text-base sm:text-lg font-semibold mb-2">
              Video Details
            </h2>
            <div className="mt-3 sm:mt-4 mb-3 sm:mb-4">
              <label className="flex items-center font-medium text-sm text-customGrayDark">
                Title<span className="text-red-500">*</span>
                <InfoTooltip content={tooltipContent.videoTitle} />
              </label>
              <input
                type="text"
                name="title"
                value={videoDetails.title}
                onChange={(e) =>
                  setVideoDetails({ ...videoDetails, title: e.target.value })
                }
                className="mt-1 block w-full border border-gray-300 bg-grayBackgroundColor focus:outline-customGreenColor rounded-lg px-3 py-2"
                placeholder="Type title here..."
              />
            </div>
            <VideoUploader
              statusId={statusId}
              setIsDisabled={setIsDisabled}
              setVideoDetails={setVideoDetails}
              isForEditVideo={isForEditVideo}
              filePath={videoDetails.filePath}
            />
          </div>
        )}

        <div
          className={`bg-white p-4 sm:p-6 lg:p-8 shadow-lg rounded-xl ${
            isViewOnly ? "mx-auto w-full max-w-3xl" : ""
          }`}
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
              {isViewOnly ? "Video" : "Video Preview"}
            </h2>
            {videoDetails.filePath && (
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                  Video Orientation: {videoOrientation || "Loading..."}
                </span>
                <span className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                  Video Format: {videoDetails.filePath.split(".").pop()}
                </span>
              </div>
            )}
          </div>

          <div className="relative w-full aspect-video bg-gray-50 rounded-xl overflow-hidden border border-gray-200 shadow-inner">
            {videoDetails.filePath ? (
              <video
                ref={videoRef}
                controls
                onLoadedMetadata={(e) => {
                  const video = e.target as HTMLVideoElement;
                  checkVideoOrientation(video);
                }}
                className="w-full h-full"
                style={{
                  background:
                    "linear-gradient(to right, #060708, #5a6066, #060708)",
                }}
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-grayBackgroundColor">
                <div className="p-6 rounded-full bg-gray-200 mb-4">
                  <MdOutlinePersonalVideo size={56} className="text-gray-400" />
                </div>
                <p className="text-gray-600 font-semibold text-center text-lg">
                  No video uploaded yet
                </p>
                <p className="text-gray-400 text-sm mt-2">
                  Upload a video to preview it here
                </p>
              </div>
            )}
          </div>

          {videoDetails.filePath && !isViewOnly && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center text-sm text-gray-600">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>
                  Video ready to be Saved click on Save button to save
                </span>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AddEditVideo;
